import { FormField } from './FormField/FormField';
import { Input, LegacyInputStatus } from './Forms/Legacy/Input/Input';
import { IndicatorsContainer } from './Forms/Legacy/Select/IndicatorsContainer';
import { NoOptionsMessage } from './Forms/Legacy/Select/NoOptionsMessage';
import { AsyncSelect, Select } from './Forms/Legacy/Select/Select';
import { Switch } from './Forms/Legacy/Switch/Switch';
import { SecretFormField } from './SecretFormField/SecretFormField';

export { Icon } from './Icon/Icon';
export { IconButton, type IconButtonVariant } from './IconButton/IconButton';
export { ConfirmButton } from './ConfirmButton/ConfirmButton';
export { DeleteButton } from './ConfirmButton/DeleteButton';
export { Tooltip } from './Tooltip/Tooltip';
export type { PopoverContent } from './Tooltip/types';
export { PopoverController } from './Tooltip/PopoverController';
export { Popover } from './Tooltip/Popover';
export { Toggletip } from './Toggletip/Toggletip';
export { Portal, getPortalContainer, PortalContainer } from './Portal/Portal';
export { CustomScrollbar, type ScrollbarPosition } from './CustomScrollbar/CustomScrollbar';
export { TabbedContainer, type TabConfig } from './TabbedContainer/TabbedContainer';

export { ClipboardButton } from './ClipboardButton/ClipboardButton';
export { Cascader, type CascaderOption } from './Cascader/Cascader';
export { ButtonCascader } from './ButtonCascader/ButtonCascader';
export { InlineToast } from './InlineToast/InlineToast';

export { LoadingPlaceholder, type LoadingPlaceholderProps } from './LoadingPlaceholder/LoadingPlaceholder';
export { LoadingBar, type LoadingBarProps } from './LoadingBar/LoadingBar';
export { ColorPicker, SeriesColorPicker } from './ColorPicker/ColorPicker';
export { ColorPickerInput } from './ColorPicker/ColorPickerInput';
export { SeriesColorPickerPopover, SeriesColorPickerPopoverWithTheme } from './ColorPicker/SeriesColorPickerPopover';
export { EmptySearchResult } from './EmptySearchResult/EmptySearchResult';
export { EmptyState } from './EmptyState/EmptyState';
export { UnitPicker } from './UnitPicker/UnitPicker';
export { StatsPicker } from './StatsPicker/StatsPicker';
export { RefreshPicker, defaultIntervals } from './RefreshPicker/RefreshPicker';
export { TimeRangePicker, type TimeRangePickerProps } from './DateTimePickers/TimeRangePicker';
export { TimeRangeProvider } from './DateTimePickers/TimeRangeContext';
export { TimePickerTooltip } from './DateTimePickers/TimeRangePicker';
export { TimeRangeLabel } from './DateTimePickers/TimeRangePicker/TimeRangeLabel';
export { TimeOfDayPicker } from './DateTimePickers/TimeOfDayPicker';
export { TimeZonePicker } from './DateTimePickers/TimeZonePicker';
export { WeekStartPicker, getWeekStart, type WeekStart } from './DateTimePickers/WeekStartPicker';
export { DatePicker, type DatePickerProps } from './DateTimePickers/DatePicker/DatePicker';
export {
  DatePickerWithInput,
  type DatePickerWithInputProps,
} from './DateTimePickers/DatePickerWithInput/DatePickerWithInput';
export { DateTimePicker } from './DateTimePickers/DateTimePicker/DateTimePicker';
export { List } from './List/List';
export { InteractiveTable, type FetchDataArgs, type FetchDataFunc } from './InteractiveTable/InteractiveTable';
export { TagsInput } from './TagsInput/TagsInput';
export { AutoSaveField } from './AutoSaveField/AutoSaveField';
export { Pagination } from './Pagination/Pagination';
export { Tag, type OnTagClick } from './Tags/Tag';
export { TagList } from './Tags/TagList';
export { FilterPill } from './FilterPill/FilterPill';

export { ConfirmModal, type ConfirmModalProps } from './ConfirmModal/ConfirmModal';
export { QueryField, type QueryFieldProps } from './QueryField/QueryField';

export { CodeEditor } from './Monaco/CodeEditor';

export { ReactMonacoEditorLazy as ReactMonacoEditor } from './Monaco/ReactMonacoEditorLazy';

export {
  type Monaco,
  type monacoTypes,
  type MonacoEditor,
  type MonacoOptions as CodeEditorMonacoOptions,
  type CodeEditorSuggestionItem,
  CodeEditorSuggestionItemKind,
} from './Monaco/types';
export { variableSuggestionToCodeEditorSuggestion } from './Monaco/utils';

// TODO: namespace
export { Modal, type Props as ModalProps } from './Modal/Modal';
export { ModalHeader } from './Modal/ModalHeader';
export { ModalTabsHeader } from './Modal/ModalTabsHeader';
export { ModalTabContent } from './Modal/ModalTabContent';
export { ModalsProvider, ModalRoot, ModalsController, ModalsContext } from './Modal/ModalsContext';
export { PageToolbar } from './PageLayout/PageToolbar';

// Renderless
export { SetInterval } from './SetInterval/SetInterval';

export { Table } from './Table/Table';
export { TableCellInspector, TableCellInspectorMode } from './Table/TableCellInspector';
export {
  type TableCustomCellOptions,
  type CustomCellRendererProps,
  type TableFieldOptions,
  type TableSortByFieldState,
  type TableFooterCalc,
  type AdHocFilterItem,
  type TableAutoCellOptions,
  type TableSparklineCellOptions,
  type TableBarGaugeCellOptions,
  type TableColoredBackgroundCellOptions,
  type TableColorTextCellOptions,
  type TableImageCellOptions,
  type TableJsonViewCellOptions,
} from './Table/types';
export { TableInputCSV } from './TableInputCSV/TableInputCSV';
export { TabsBar } from './Tabs/TabsBar';
export { Tab, type TabProps } from './Tabs/Tab';
export { VerticalTab } from './Tabs/VerticalTab';
export { TabContent } from './Tabs/TabContent';
export { Counter } from './Tabs/Counter';
export { RenderUserContentAsHTML } from './RenderUserContentAsHTML/RenderUserContentAsHTML';

// Visualizations
export {
  BigValue,
  BigValueColorMode,
  BigValueGraphMode,
  BigValueJustifyMode,
  BigValueTextMode,
} from './BigValue/BigValue';
export { Sparkline } from './Sparkline/Sparkline';

export { Gauge } from './Gauge/Gauge';
export { BarGauge } from './BarGauge/BarGauge';
export {
  VizTooltip,
  VizTooltipContainer,
  SeriesTable,
  type SeriesTableProps,
  SeriesTableRow,
  type SeriesTableRowProps,
} from './VizTooltip';
export { VizRepeater, type VizRepeaterRenderValueProps } from './VizRepeater/VizRepeater';
export {
  PanelChrome,
  type PanelChromeProps,
  type PanelPadding,
  type PanelChromeType,
  PanelChromeLoadingIndicator,
  type PanelChromeLoadingIndicatorProps,
  PanelContextProvider,
  type PanelContext,
  PanelContextRoot,
  usePanelContext,
} from './PanelChrome';
export {
  VizLayout,
  type VizLayoutComponentType,
  type VizLayoutLegendProps,
  type VizLayoutProps,
} from './VizLayout/VizLayout';
export { type VizLegendItem, SeriesVisibilityChangeBehavior } from './VizLegend/types';
export { VizLegend } from './VizLegend/VizLegend';
export { VizLegendListItem } from './VizLegend/VizLegendListItem';

export { Alert, type AlertVariant } from './Alert/Alert';
export { GraphSeriesToggler, type GraphSeriesTogglerAPI } from '../graveyard/Graph/GraphSeriesToggler';
export { Collapse, ControlledCollapse } from './Collapse/Collapse';
export { CollapsableSection } from './Collapse/CollapsableSection';
export { DataLinkButton } from './DataLinks/DataLinkButton';
export { FieldLinkList } from './DataLinks/FieldLinkList';
// Panel editors
export { FullWidthButtonContainer } from './Button/FullWidthButtonContainer';
export { ClickOutsideWrapper } from './ClickOutsideWrapper/ClickOutsideWrapper';
export * from './SingleStatShared/index';
export { CallToActionCard } from './CallToActionCard/CallToActionCard';
export { ContextMenu, type ContextMenuProps } from './ContextMenu/ContextMenu';
export { Menu, type MenuProps } from './Menu/Menu';
export { MenuGroup, type MenuItemsGroup, type MenuGroupProps } from './Menu/MenuGroup';
export { MenuItem, type MenuItemProps } from './Menu/MenuItem';
export { WithContextMenu } from './ContextMenu/WithContextMenu';
export { DataLinksInlineEditor } from './DataLinks/DataLinksInlineEditor/DataLinksInlineEditor';
export { DataLinkInput } from './DataLinks/DataLinkInput';
export {
  DataLinksContextMenu,
  type DataLinksContextMenuProps,
  type DataLinksContextMenuApi,
} from './DataLinks/DataLinksContextMenu';
export { SeriesIcon } from './VizLegend/SeriesIcon';
export { InfoBox } from './InfoBox/InfoBox';
export { FeatureInfoBox } from './InfoBox/FeatureInfoBox';
export { FeatureBadge } from './FeatureBadge/FeatureBadge';

export { JSONFormatter } from './JSONFormatter/JSONFormatter';
export { JsonExplorer } from './JSONFormatter/json_explorer/json_explorer';
export {
  ErrorBoundary,
  ErrorBoundaryAlert,
  type ErrorBoundaryAlertProps,
  withErrorBoundary,
} from './ErrorBoundary/ErrorBoundary';
export { ErrorWithStack } from './ErrorBoundary/ErrorWithStack';
export { DataSourceHttpSettings } from './DataSourceSettings/DataSourceHttpSettings';
export { CustomHeadersSettings } from './DataSourceSettings/CustomHeadersSettings';
export { AlertingSettings } from './DataSourceSettings/AlertingSettings';
export { SecureSocksProxySettings } from './DataSourceSettings/SecureSocksProxySettings';
export { TLSAuthSettings } from './DataSourceSettings/TLSAuthSettings';
export { CertificationKey } from './DataSourceSettings/CertificationKey';
export { Spinner } from './Spinner/Spinner';
export { FadeTransition } from './transitions/FadeTransition';
export { SlideOutTransition } from './transitions/SlideOutTransition';
export { Segment, SegmentAsync, SegmentInput, SegmentSelect, SegmentSection } from './Segment/';
export { Drawer } from './Drawer/Drawer';
export { Slider } from './Slider/Slider';
export { RangeSlider } from './Slider/RangeSlider';

// Next-gen forms
export { Form } from './Forms/Form';
export { sharedInputStyle } from './Forms/commonStyles';
export { InputControl } from './InputControl';
export { Button, LinkButton, type ButtonVariant, ButtonGroup, type ButtonProps, clearButtonStyles } from './Button';
export { ToolbarButton, ToolbarButtonRow } from './ToolbarButton';
export { ValuePicker } from './ValuePicker/ValuePicker';
export { fieldMatchersUI } from './MatchersUI/fieldMatchersUI';
export { Link } from './Link/Link';
export { TextLink } from './Link/TextLink';
export { Text } from './Text/Text';
export { Box } from './Layout/Box/Box';
export { Stack } from './Layout/Stack/Stack';
export { Grid } from './Layout/Grid/Grid';
export { Space } from './Layout/Space';
export { ScrollContainer } from './ScrollContainer/ScrollContainer';

export { Label } from './Forms/Label';
export { Field, type FieldProps } from './Forms/Field';
export { Legend } from './Forms/Legend';
export { FieldSet } from './Forms/FieldSet';
export { FieldValidationMessage } from './Forms/FieldValidationMessage';
export { InlineField } from './Forms/InlineField';
export { InlineSegmentGroup } from './Forms/InlineSegmentGroup';
export { InlineLabel } from './Forms/InlineLabel';
export { InlineFieldRow } from './Forms/InlineFieldRow';
export { FieldArray } from './Forms/FieldArray';

// Select
// Note - Select is nearly deprecated in favor of Combobox
export { default as resetSelectStyles } from './Select/resetSelectStyles';
export * from './Select/Select';
export { SelectMenuOptions } from './Select/SelectMenu';
export { getSelectStyles } from './Select/getSelectStyles';
export * from './Select/types';

export { Combobox, type ComboboxOption } from './Combobox/Combobox';

export { HorizontalGroup, VerticalGroup, Container } from './Layout/Layout';
export { Badge, type BadgeColor, type BadgeProps } from './Badge/Badge';
export { RadioButtonGroup } from './Forms/RadioButtonGroup/RadioButtonGroup';
export { RadioButtonDot } from './Forms/RadioButtonList/RadioButtonDot';
export { RadioButtonList } from './Forms/RadioButtonList/RadioButtonList';

export { Input, getInputStyles } from './Input/Input';
export { AutoSizeInput } from './Input/AutoSizeInput';
export { FilterInput } from './FilterInput/FilterInput';
export type { FormInputSize } from './Forms/types';
export * from './SecretInput';
export * from './SecretTextArea';

export { Switch, InlineSwitch } from './Switch/Switch';
export { Checkbox } from './Forms/Checkbox';

export { TextArea } from './TextArea/TextArea';
export { FileUpload } from './FileUpload/FileUpload';
export * from './FileDropzone';
export { TimeRangeInput } from './DateTimePickers/TimeRangeInput';
export { RelativeTimeRangePicker } from './DateTimePickers/RelativeTimeRangePicker/RelativeTimeRangePicker';
export { Card, type Props as CardProps, getCardStyles } from './Card/Card';
export { CardContainer, type CardContainerProps } from './Card/CardContainer';
export { FormattedValueDisplay } from './FormattedValueDisplay/FormattedValueDisplay';
export { ButtonSelect } from './Dropdown/ButtonSelect';
export { Dropdown } from './Dropdown/Dropdown';
export { PluginSignatureBadge, type PluginSignatureBadgeProps } from './PluginSignatureBadge/PluginSignatureBadge';
export { UserIcon, type UserIconProps } from './UsersIndicator/UserIcon';
export { type UserView } from './UsersIndicator/types';
export { Avatar } from './UsersIndicator/Avatar';
// Export this until we've figured out a good approach to inline form styles.
export { InlineFormLabel } from './FormLabel/FormLabel';
export { Divider } from './Divider/Divider';
export { getDragStyles, type DragHandlePosition } from './DragHandle/DragHandle';
export { useSplitter } from './Splitter/useSplitter';

/** @deprecated Please use non-legacy versions of these components */
const LegacyForms = {
  SecretFormField,
  FormField,
  Select,
  AsyncSelect,
  IndicatorsContainer,
  NoOptionsMessage,
  Input,
  Switch,
};
export { LegacyForms, LegacyInputStatus };

// WIP, need renames and exports cleanup
export * from './uPlot/config';
export { ScaleDistribution, BarGaugeDisplayMode } from '@grafana/schema';
export { UPlotConfigBuilder } from './uPlot/config/UPlotConfigBuilder';
export { UPLOT_AXIS_FONT_SIZE } from './uPlot/config/UPlotAxisBuilder';
export { UPlotChart } from './uPlot/Plot';
export { PlotLegend } from './uPlot/PlotLegend';
export * from './uPlot/geometries';
export * from './uPlot/plugins';
export { type PlotTooltipInterpolator, type PlotSelection, FIXED_UNIT } from './uPlot/types';
export { type UPlotConfigPrepFn } from './uPlot/config/UPlotConfigBuilder';
export * from './PanelChrome/types';
export { Label as BrowserLabel } from './BrowserLabel/Label';
export { PanelContainer } from './PanelContainer/PanelContainer';

// -----------------------------------------------------
// Graveyard: exported, but no longer used internally
// These will be removed in the future
// -----------------------------------------------------

export { Graph } from '../graveyard/Graph/Graph';
export { GraphWithLegend } from '../graveyard/Graph/GraphWithLegend';
export { GraphContextMenu, GraphContextMenuHeader } from '../graveyard/Graph/GraphContextMenu';
export { graphTimeFormat, graphTickFormatter } from '../graveyard/Graph/utils';

export { GraphNG, type GraphNGProps } from '../graveyard/GraphNG/GraphNG';
export { TimeSeries } from '../graveyard/TimeSeries/TimeSeries';
export { useGraphNGContext } from '../graveyard/GraphNG/hooks';
export { preparePlotFrame, buildScaleKey } from '../graveyard/GraphNG/utils';
export { type GraphNGLegendEvent } from '../graveyard/GraphNG/types';

export { ZoomPlugin } from '../graveyard/uPlot/plugins/ZoomPlugin';
export { TooltipPlugin } from '../graveyard/uPlot/plugins/TooltipPlugin';

export {
  ElementSelectionContext,
  useElementSelection,
  type ElementSelectionContextState,
  type ElementSelectionContextItem,
} from './ElementSelectionContext/ElementSelectionContext';
